import { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Socket, io } from 'socket.io-client';
// custom hooks
import { useAppDispatch } from 'redux-store/hooks/useAppDispatch';
import { useAppSelector } from 'redux-store/hooks/useAppSelector';
// api
import {
  ActivityFeedPostTypeEnum,
  ISocketComment,
  ISocketDeleteCommentData,
  ISocketDeletePostData,
  ISocketPost,
  ISocketSelectionNotification,
  SelectionItemsEventEnum,
  SelectionNotificationEventEnum,
  TActivityFeedEvent,
  TSocketNotification,
  TUpdatedUploadEvent,
  activityFeedApi,
} from 'redux-store/api/activity-feed';
import { useGetMeQuery } from 'redux-store/api/auth';
import {
  IGetUnviewedCountsArgs,
  IUnviewedItem,
  UNVIEWED_ITEMS_SERIALIZATION_KEY,
  projectsApi,
} from 'redux-store/api/projects';
import {
  ICostCode,
  ISelectionItem,
  IUpdatedSelectionItemMeta,
  SelectionPhaseStatusesEnum,
  SelectionStatusEnum,
  TApprovedSelectionItemMeta,
  TDeletedSelectionItemMeta,
  selectionsApi,
} from 'redux-store/api/selections';
// slices
import {
  addChangeDraftUnreadId,
  addDraftUnreadId,
  deleteDraft,
  deleteDraftUnreadId,
  setDraft,
  updateDraft,
} from 'redux-store/slices/draft-slice';
import {
  addComment,
  deleteComments as deleteLocalComment,
  editComment,
  editCommentStatus,
  likeUnlikeComment as likeUnlikeLocalComment,
} from 'redux-store/slices/new-comments-slice';
import { handlePostReplyDefault } from 'redux-store/slices/reply-to-post-slice';
// selectors
import { authSelector, queryArgsSelector } from 'redux-store/selector';
// helpers
import { DefaultEventsMap } from '@socket.io/component-emitter';
import { ICompany } from '_interfaces/company';
import dayjs from 'dayjs';
import {
  convertToCommentDraft,
  convertToPostDraft,
  isDraft,
} from 'redux-store/api/activity-feed/helpers';
import { ICompanyBillingPlan, billingApi } from 'redux-store/api/billing';
import { getTotalSelectionsAmount } from 'redux-store/api/selections/helpers';
import { setSubscribedPlanInfo } from 'redux-store/slices/billing-slice';
import { setDefaultArgs } from 'redux-store/slices/query-args-slice';
import {
  selectSelection,
  unselectCostCode,
  unselectSelection,
  updateSelectedSelection,
} from 'redux-store/slices/selected-selections-slice';
import { RootState, store } from 'redux-store/store';
import { httpToWs } from 'utils/httpToWs';
import { getEnvironmentConfig } from '../utils/environment';

const { apiUrl } = getEnvironmentConfig();
const socketUrl = `${httpToWs(apiUrl)}`;
const activityFeedUrl = `activity-feed`;
const companyBillingUrl = `company-billing-plans`;

enum SocketEvents {
  CREATED = 'created',
  UPDATED = 'updated',
  USER_JOINED_EVENT = 'user_joined',
  SEND_MESSAGE_EVENT = 'send_message',
  ERROR_EVENT = 'user_error',
  DISCONNECT_EVENT = 'user_disconnect',
  ADD_TO_PROJECT_EVENT = 'USER_JOINING_TO_ROOM',
  JOIN_TO_ROOM_EVENT = 'join_to_room_event',
}

enum ChangeNotificationCountType {
  INCREASE = 'increase',
  DECREASE = 'decrease',
}

interface IJoinEvent {
  msg: string;
  rooms: string;
  socketId: string;
}

const isEventTypeSelection = (data: TSocketNotification): data is ISocketSelectionNotification => {
  return Object.values(SelectionNotificationEventEnum).some((event) => event === data.event);
};

export const useSocketListener = (): null => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname, search } = useLocation();
  const { isAuth } = useAppSelector(authSelector);
  const { projects: projectsArgs } = useAppSelector(queryArgsSelector);

  const isAccountDashboard = pathname.startsWith('/account-dashboard');
  const isProjectDashboard = pathname.startsWith('/project-dashboard');
  const isSelectionPage = pathname.startsWith('/selections/');

  const projectId =
    pathname.startsWith('/project-dashboard') || pathname.startsWith('/selections')
      ? Number(pathname.split('/')[2])
      : null;
  const companyId = localStorage.getItem('company-id');

  const { data: userMe } = useGetMeQuery(undefined, { skip: !isAuth });
  const { data: projectsData } = projectsApi.endpoints.getProjects.useQueryState(projectsArgs, {
    skip: !isAccountDashboard,
  });

  const projectIds = projectsData?.items?.map(({ id }) => id);
  const activityFeedSocket = useRef<ReturnType<typeof io> | null>(null);
  const companyBillingSocket = useRef<ReturnType<typeof io> | null>(null);

  const CreateSocket = (
    token: string,
    url: string,
    socket: React.RefObject<Socket<DefaultEventsMap, DefaultEventsMap>>
  ) => {
    socket.current = io(`${socketUrl}${url}`, {
      auth: {
        token: token,
      },
      query: {
        companyId,
      },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
      transports: ['websocket', 'polling'],
    });

    socket.current.on('connect_error', (error) => {
      console.error(`(${url}) Socket connection error:`, error);
      if (error.message.includes('Session ID unknown')) {
        setTimeout(() => {
          if (socket.current) {
            socket.current.disconnect();
            socket.current.connect();
          }
        }, 1000);
      }
    });

    socket.current.on(SocketEvents.ERROR_EVENT, (error) => {
      console.error(`(${url}) WebSocket authentication error:`, error);
      // Handle authentication errors
      if (error === 'Unauthorized' || error === 'Invalid token' || error === 'User not found') {
        console.warn(`(${url}) Authentication failed, disconnecting socket`);
        if (socket.current) {
          socket.current.disconnect();
        }
      }
    });

    // Add connection state monitoring
    socket.current.on('connect', () => {
      console.log(`(${url}) Socket connected successfully`);
    });

    socket.current.on('disconnect', (reason) => {
      console.log(`(${url}) Socket disconnected:`, reason);
    });
  };

  useEffect(() => {
    if (companyId && isAuth) {
      const token = localStorage.getItem('Authorization');
      if (!token) {
        console.warn('No authorization token found, skipping socket connection');
        return;
      }

      CreateSocket(token, activityFeedUrl, activityFeedSocket);
      CreateSocket(token, companyBillingUrl, companyBillingSocket);
    }

    return () => {
      if (activityFeedSocket.current && activityFeedSocket.current.connected) {
        activityFeedSocket.current.disconnect();
      }

      if (companyBillingSocket.current && companyBillingSocket.current.connected) {
        companyBillingSocket.current.disconnect();
      }
    };
  }, [companyId, isAuth]);

  useEffect(() => {
    const addNotification = (
      unviewedItemType: keyof IUnviewedItem,
      eventProjectId: number,
      cacheKey: number[],
      filterCondition: boolean = true,
      type: string = ChangeNotificationCountType.DECREASE
    ) => {
      if (filterCondition) {
        dispatch(
          projectsApi.util.updateQueryData(
            'getUnviewedCounts',
            { UNVIEWED_ITEMS_SERIALIZATION_KEY } as unknown as IGetUnviewedCountsArgs,
            (draft) => {
              if (typeof draft?.items?.[eventProjectId]?.[unviewedItemType] === 'number') {
                if (type === ChangeNotificationCountType.INCREASE) {
                  if (draft.items[eventProjectId][unviewedItemType] === 0) {
                    draft.items[eventProjectId][unviewedItemType] = 0;
                  } else {
                    draft.items[eventProjectId][unviewedItemType] -= 1;
                  }
                }

                if (type == ChangeNotificationCountType.DECREASE) {
                  draft.items[eventProjectId][unviewedItemType] += 1;
                }
              }
            }
          )
        );
      }
    };

    const addPost = (data: ISocketPost, eventProjectId: number) => {
      dispatch(
        activityFeedApi.util.updateQueryData('getPosts', { projectId: eventProjectId }, (draft) => {
          if (draft?.items) {
            draft.items = [
              {
                ...data,
                isViewed: userMe.id === data?.data?.profile?.id,
              },
              ...draft.items,
            ];
          }
        })
      );
    };

    const updatePost = (data: ISocketPost, eventProjectId: number) => {
      dispatch(
        activityFeedApi.util.updateQueryData('getPosts', { projectId: eventProjectId }, (draft) => {
          if (draft?.items) {
            const postIndex = draft.items.findIndex(({ id }) => id === data.id);
            if (postIndex >= 0) {
              draft.items[postIndex] = Object.assign(draft.items[postIndex], data);
            }
          }
        })
      );

      const postId = Number(searchParams.get('postId'));

      if (postId) {
        dispatch(
          activityFeedApi.util.updateQueryData(
            'getPostById',
            { projectId: eventProjectId, postId },
            (draft) => {
              if (draft) {
                draft = Object.assign(draft, data);
              }
            }
          )
        );
      }
    };

    const updateComment = (data: ISocketComment) => {
      dispatch(editComment(data));

      dispatch(
        activityFeedApi.util.updateQueryData('getComments', { postId: data.postId }, (draft) => {
          if (draft?.items) {
            if (data.rootCommentId) {
              const rootIndex = draft.items.findIndex(({ id }) => id === data.rootCommentId);
              if (rootIndex >= 0) {
                const commentIndex = draft.items[rootIndex]?.replies?.findIndex(
                  ({ id: replyId }) => replyId === data.id
                );
                if (commentIndex >= 0) {
                  draft.items[rootIndex].replies[commentIndex] = Object.assign(
                    draft.items[rootIndex].replies[commentIndex],
                    data
                  );
                }
              }
            } else {
              const commentIndex = draft.items.findIndex(({ id }) => data.id);
              if (commentIndex >= 0) {
                draft.items[commentIndex] = Object.assign(draft.items[commentIndex], data);
              }
            }
          }
        })
      );
    };

    const deletePost = (activityFeedItemId: number, eventProjectId: number) => {
      dispatch(
        activityFeedApi.util.updateQueryData('getPosts', { projectId: eventProjectId }, (draft) => {
          if (draft?.items) {
            draft.items = draft.items?.filter(({ id }) => id !== activityFeedItemId);
          }
        })
      );

      if (activityFeedItemId === Number(searchParams.get('postId'))) {
        searchParams.delete('postId');
        searchParams.delete('projectId');
        setSearchParams(searchParams);
        dispatch(handlePostReplyDefault());
      }
    };

    const deleteComment = ({
      activityFeedItemId,
      postId,
      eventProjectId,
      rootCommentId,
      deletedCommentsCount,
    }: {
      activityFeedItemId: number;
      postId: number;
      eventProjectId?: number;
      rootCommentId?: number;
      deletedCommentsCount: number;
    }) => {
      dispatch(
        deleteLocalComment({
          id: activityFeedItemId,
          postId,
          rootCommentId,
        })
      );

      dispatch(
        activityFeedApi.util.updateQueryData('getComments', { postId }, (draft) => {
          if (draft?.items) {
            if (rootCommentId) {
              const rootIndex = draft.items.findIndex(({ id }) => id === rootCommentId);
              if (rootIndex >= 0 && draft.items[rootIndex]?.replies?.length) {
                draft.items[rootIndex].replies = draft.items[rootIndex].replies?.filter(
                  ({ id: commentId }) => commentId !== activityFeedItemId
                );
              }
            } else {
              draft.items = draft.items?.filter(
                ({ id: commentId }) => commentId !== activityFeedItemId
              );
            }
          }
        })
      );

      dispatch(
        activityFeedApi.util.updateQueryData('getPosts', { projectId: eventProjectId }, (draft) => {
          if (draft?.items) {
            const postIndex = draft.items.findIndex(({ id }) => id === postId);
            if (
              postIndex >= 0 &&
              draft.items[postIndex].type === ActivityFeedPostTypeEnum.POST &&
              draft.items[postIndex].commentsCount
            ) {
              draft.items[postIndex].commentsCount -= deletedCommentsCount;
            }
          }
        })
      );

      if (postId === Number(searchParams.get('postId'))) {
        dispatch(
          activityFeedApi.util.updateQueryData(
            'getPostById',
            { postId, projectId: eventProjectId },
            (draft) => {
              if (draft && draft.commentsCount) {
                draft.commentsCount -= deletedCommentsCount;
              }
            }
          )
        );
      }
    };

    const likeUnlikePost = (
      activityFeedItemId: number,
      eventProjectId: number,
      actionType: 'like' | 'unlike'
    ) => {
      dispatch(
        activityFeedApi.util.updateQueryData('getPosts', { projectId: eventProjectId }, (draft) => {
          if (draft?.items) {
            const postIndex = draft.items?.findIndex(({ id }) => id === activityFeedItemId);
            if (postIndex >= 0 && draft.items[postIndex].type === ActivityFeedPostTypeEnum.POST) {
              if (actionType === 'like') {
                draft.items[postIndex].likesCount += 1;
              } else {
                draft.items[postIndex].likesCount -= 1;
              }
            }
          }
        })
      );

      if (activityFeedItemId === Number(searchParams.get('postId'))) {
        dispatch(
          activityFeedApi.util.updateQueryData(
            'getPostById',
            { postId: activityFeedItemId, projectId: eventProjectId },
            (draft) => {
              if (draft) {
                if (actionType === 'like') {
                  draft.likesCount += 1;
                } else {
                  draft.likesCount -= 1;
                }
              }
            }
          )
        );
      }
    };

    const likeUnlikeComment = (
      activityFeedItemId: number,
      eventProjectId: number,
      postId: number,
      actionType: 'like' | 'unlike',
      rootCommentId?: number
    ) => {
      dispatch(
        likeUnlikeLocalComment({
          activityFeedItemId,
          postId,
          rootCommentId,
          commentId: activityFeedItemId,
          projectId: eventProjectId,
          increase: actionType === 'like',
          fromSocket: true,
        })
      );

      dispatch(
        activityFeedApi.util.updateQueryData('getComments', { postId }, (draft) => {
          if (draft?.items) {
            if (rootCommentId) {
              const rootIndex = draft.items?.findIndex(({ id }) => id === rootCommentId);
              if (rootIndex >= 0 && draft.items[rootIndex]?.replies?.length) {
                const commentIndex = draft.items[rootIndex].replies?.findIndex(
                  ({ activityFeedItemId: commentId }) => commentId === activityFeedItemId
                );
                if (commentIndex >= 0) {
                  if (actionType === 'like') {
                    draft.items[rootIndex].replies[commentIndex].likesCount += 1;
                  } else {
                    draft.items[rootIndex].replies[commentIndex].likesCount -= 1;
                  }
                }
              }
            } else {
              const commentIndex = draft.items?.findIndex(
                ({ activityFeedItemId: commentId }) => commentId === activityFeedItemId
              );
              if (commentIndex >= 0) {
                if (actionType === 'like') {
                  draft.items[commentIndex].likesCount += 1;
                } else {
                  draft.items[commentIndex].likesCount -= 1;
                }
              }
            }
          }
        })
      );
    };

    const increasePostCommentsCount = (
      data: ISocketComment,
      eventProjectId: number,
      postId?: number
    ) => {
      if (postId) {
        dispatch(
          activityFeedApi.util.updateQueryData(
            'getPostById',
            { projectId: eventProjectId, postId },
            (draft) => {
              if (draft) {
                draft.commentsCount += 1;
              }
            }
          )
        );
      } else {
        dispatch(
          activityFeedApi.util.updateQueryData(
            'getPosts',
            { projectId: eventProjectId },
            (draft) => {
              if (draft?.items) {
                const index = draft.items?.findIndex(({ id }) => id === data?.postId);
                if (index >= 0 && draft.items[index].type === ActivityFeedPostTypeEnum.POST) {
                  draft.items[index].commentsCount += 1;
                }
              }
            }
          )
        );
      }
    };

    const addCommentToPost = (data: ISocketComment, postId: number) => {
      dispatch(
        activityFeedApi.util.updateQueryData('getComments', { postId }, (draft) => {
          if (draft?.items) {
            if (data?.rootCommentId) {
              const index = draft.items?.findIndex(({ id }) => id === data?.rootCommentId);
              if (index >= 0) {
                draft.items[index]?.replies?.push({
                  ...data,
                  isView: userMe.id === data?.profile?.id,
                });
              }
            } else {
              draft.items?.unshift({
                ...data,
                isView: userMe.id === data?.profile?.id,
                replies: [],
              });
            }
          }
        })
      );
    };

    const addLocalCommentToPost = (data: ISocketComment, isView: boolean) => {
      dispatch(
        addComment({
          ...data,
          isView,
          isLiked: false,
          likesCount: 0,
        })
      );
    };

    // const handleSelectionEvent = (data: ISocketSelectionNotification) => {
    //   if (data.selectionItem) {
    //     const receivedSelection = data.selectionItem;
    //     dispatch(
    //       selectionsApi.util.updateQueryData(
    //         'getCostCodesWithSelections',
    //         { projectId },
    //         (draft) => {
    //           if (draft?.items) {
    //             const categoryIndex = draft.items.findIndex(
    //               ({ id }) => id === receivedSelection?.costCode?.id
    //             );

    //             if (categoryIndex >= 0) {
    //               // new item
    //               if (data.event === SelectionNotificationEventEnum.SELECTION_ITEM_HAS_BEEN_ADDED) {
    //                 if (receivedSelection.status === SelectionStatusEnum.APPROVED) {
    //                   const approvedOption = receivedSelection.options.find(
    //                     ({ isSelected }) => isSelected
    //                   );
    //                   if (approvedOption) {
    //                     draft.items[categoryIndex].approvedSelectionAmountDecimalForm =
    //                       getTotalSelectionsAmount(
    //                         draft.items[categoryIndex].approvedSelectionAmountDecimalForm,
    //                         Number(approvedOption.amount) * approvedOption.quantity,
    //                         'sum'
    //                       );
    //                   }
    //                 }
    //                 draft.items[categoryIndex].selections.unshift(receivedSelection);
    //                 dispatch(selectionsApi.util.invalidateTags(['Selections Summary']));
    //               }
    //               // approve item
    //               if (
    //                 data.event === SelectionNotificationEventEnum.SELECTION_ITEM_HAS_BEEN_APPROVED
    //               ) {
    //                 const selectionIndex = draft.items[categoryIndex].selections?.findIndex(
    //                   ({ id: sId }) => sId === receivedSelection.id
    //                 );
    //                 if (selectionIndex >= 0) {
    //                   draft.items[categoryIndex].selections[selectionIndex] = receivedSelection;

    //                   const approvedOption = receivedSelection.options.find(
    //                     ({ isSelected }) => isSelected
    //                   );

    //                   if (approvedOption) {
    //                     draft.items[categoryIndex].approvedSelectionAmountDecimalForm =
    //                       getTotalSelectionsAmount(
    //                         draft.items[categoryIndex].approvedSelectionAmountDecimalForm,
    //                         Number(approvedOption.amount) * approvedOption.quantity,
    //                         'sum'
    //                       );
    //                   }

    //                   dispatch(selectionsApi.util.invalidateTags(['Selections Summary']));
    //                 }
    //               }
    //             }
    //           }
    //         }
    //       )
    //     );
    //   }
    // };

    const addPostDraft = (eventData: ISocketPost, eventProjectId: number) => {
      if (isProjectDashboard) {
        dispatch(setDraft(convertToPostDraft(eventData, eventProjectId)));
        return;
      }

      dispatch(
        activityFeedApi.util.updateQueryData('getDrafts', undefined, (draft) => {
          if (draft?.items) {
            draft?.items?.unshift(convertToPostDraft(eventData, eventProjectId));
          }
        })
      );

      dispatch(addDraftUnreadId(eventData.id));
    };

    const updatePostDraft = (eventData: ISocketPost, eventProjectId: number) => {
      const state = store.getState();
      const drafts = activityFeedApi.endpoints.getDrafts.select(undefined)(state);
      const items = drafts?.data?.items ?? [];
      const postIndex = items?.findIndex(({ id }) => id === eventData.id);

      if (postIndex >= 0) {
        dispatch(
          activityFeedApi.util.updateQueryData('getDrafts', undefined, (draft) => {
            draft.items[postIndex] = Object.assign(
              draft.items[postIndex],
              convertToPostDraft(eventData, eventProjectId)
            );
          })
        );

        dispatch(addChangeDraftUnreadId(eventData.id));
      } else {
        dispatch(updateDraft(convertToPostDraft(eventData, eventProjectId)));
      }
    };

    const deletePostDraft = (eventData: ISocketDeletePostData) => {
      const activityFeedItemId = eventData?.activityFeedItemIds?.[0]?.activityFeedItemId;

      if (!activityFeedItemId) return;

      const state = store.getState();
      const drafts = activityFeedApi.endpoints.getDrafts.select(undefined)(state);
      const items = drafts?.data?.items ?? [];

      const itemExists = items.some((item) => item.id === activityFeedItemId);

      if (itemExists) {
        dispatch(
          activityFeedApi.util.updateQueryData('getDrafts', undefined, (draft) => {
            if (activityFeedItemId && draft?.items) {
              draft.items = draft.items?.filter(({ id }) => id !== activityFeedItemId);
            }
          })
        );
        dispatch(deleteDraftUnreadId(activityFeedItemId));
      } else {
        dispatch(deleteDraft({ id: activityFeedItemId, type: 'DELETED_POST' }));
      }
    };

    const addCommentDraft = (eventData: ISocketComment, eventProjectId: number) => {
      if (isProjectDashboard) {
        dispatch(setDraft(convertToCommentDraft(eventData, eventProjectId)));
        return;
      }

      dispatch(
        activityFeedApi.util.updateQueryData('getDrafts', undefined, (draft) => {
          if (draft?.items) {
            draft?.items?.unshift(convertToCommentDraft(eventData, eventProjectId));
          }
        })
      );

      dispatch(addDraftUnreadId(eventData.id));
    };

    const updateCommentDraft = (eventData: ISocketComment, eventProjectId: number) => {
      const state = store.getState();
      const drafts = activityFeedApi.endpoints.getDrafts.select(undefined)(state);
      const items = drafts?.data?.items ?? [];
      const commentIndex = items?.findIndex(({ data }) => data.id === eventData.id);
      if (commentIndex >= 0) {
        dispatch(
          activityFeedApi.util.updateQueryData('getDrafts', undefined, (draft) => {
            draft.items[commentIndex] = Object.assign(
              draft.items[commentIndex],
              convertToCommentDraft(eventData, eventProjectId)
            );
          })
        );
        dispatch(addChangeDraftUnreadId(eventData.id));
      } else {
        dispatch(updateDraft(convertToCommentDraft(eventData, eventProjectId)));
      }
    };

    const deleteCommentDraft = (eventData: ISocketDeleteCommentData) => {
      const activityFeedItemId = eventData?.activityFeedItemIds?.[0]?.activityFeedItemId;

      if (!activityFeedItemId) return;

      const state = store.getState();
      const drafts = activityFeedApi.endpoints.getDrafts.select(undefined)(state);
      const items = drafts?.data?.items ?? [];
      const itemExists = items.some(({ data }) => data.id === activityFeedItemId);

      if (itemExists) {
        dispatch(
          activityFeedApi.util.updateQueryData('getDrafts', undefined, (draft) => {
            if (activityFeedItemId && draft?.items) {
              draft.items = draft.items?.filter(({ data }) => data.id !== activityFeedItemId);
            }
          })
        );
        dispatch(deleteDraftUnreadId(activityFeedItemId));
      } else {
        dispatch(deleteDraft({ id: activityFeedItemId, type: 'DELETED_COMMENT' }));
      }
    };

    const getIsMe = (profileId: number) => {
      return profileId === userMe?.id;
    };

    const processDraftEvent = (eventData: TActivityFeedEvent): void => {
      switch (eventData.type) {
        case 'NEW_POST':
          if (getIsMe(eventData?.data?.data?.profile?.id)) {
            addPostDraft(eventData.data, eventData.projectId);
          }
          break;
        case 'CHANGED_POST':
          if (getIsMe(eventData?.data?.data?.profile?.id)) {
            updatePostDraft(eventData.data, eventData.projectId);
          }
          break;
        case 'DELETED_POST':
          if (getIsMe(eventData?.data?.profileId)) {
            deletePostDraft(eventData.data);
          }
          break;
        case 'NEW_COMMENT':
          if (getIsMe(eventData?.data?.profile?.id)) {
            addCommentDraft(eventData.data, eventData.projectId);
          }
          break;
        case 'CHANGED_COMMENT':
          if (getIsMe(eventData?.data?.profile?.id)) {
            updateCommentDraft(eventData.data, eventData.projectId);
          }
          break;
        case 'DELETED_COMMENT':
          if (getIsMe(eventData?.data?.profileId)) {
            deleteCommentDraft(eventData.data);
          }
          break;
      }
    };

    const processUpdatedUpload = (eventData: TUpdatedUploadEvent) => {
      const { projectId: eventProjectId } = eventData;
      const { postId, uploadId, uploadStatus, commentId, rootCommentId } = eventData.data;

      const isPost = !commentId;
      const isComment = !!commentId;
      const searchParamsPostId = Number(searchParams.get('postId'));

      if (isPost) {
        dispatch(
          activityFeedApi.util.updateQueryData(
            'getPosts',
            { projectId: eventProjectId },
            (draft) => {
              const post = draft?.items?.find(({ id }) => id === postId);
              if (post.type === ActivityFeedPostTypeEnum.POST) {
                const upload = post?.data?.uploads?.find(({ id }) => id === uploadId);

                if (upload) {
                  upload.status = uploadStatus;
                }
              }
            }
          )
        );

        if (searchParamsPostId) {
          dispatch(
            activityFeedApi.util.updateQueryData(
              'getPostById',
              { projectId: eventProjectId, postId },
              (draft) => {
                if (draft) {
                  const upload = draft?.data?.uploads?.find(({ id }) => id === uploadId);

                  if (upload) {
                    upload.status = uploadStatus;
                  }
                }
              }
            )
          );
        }
      }

      if (isComment) {
        dispatch(editCommentStatus({ commentId, rootCommentId, status: uploadStatus, uploadId }));

        dispatch(
          activityFeedApi.util.updateQueryData('getComments', { postId }, (draft) => {
            if (draft?.items) {
              if (rootCommentId) {
                const index = draft.items.findIndex(({ id }) => id === rootCommentId);
                if (index >= 0) {
                  const comment = draft.items[index]?.replies.find(({ id }) => id === commentId);
                  const upload = comment?.uploads?.find(({ id }) => id === uploadId);

                  if (upload) {
                    upload.status = uploadStatus;
                  }
                }
              } else {
                const comment = draft.items.find(({ id }) => id === commentId);
                const upload = comment?.uploads?.find(({ id }) => id === uploadId);

                if (upload) {
                  upload.status = uploadStatus;
                }
              }
            }
          })
        );
      }
    };

    const invalidateSelectionSummary = () => {
      dispatch(selectionsApi.util.invalidateTags(['Selections Summary']));
    };

    const cloneProjectSelection = ({
      response,
      projectId,
    }: {
      response: ISelectionItem;
      projectId: number;
    }) => {
      invalidateSelectionSummary();

      dispatch(
        selectionsApi.util.updateQueryData(
          'getCostCodesWithSelections',
          {
            projectId,
          },
          (draft) => {
            if (draft?.items) {
              const categoryIndex = draft.items.findIndex(({ id }) => id === response.costCode.id);
              if (categoryIndex >= 0) {
                // update approved amount
                if (response?.status === SelectionStatusEnum.APPROVED) {
                  const approvedOption = response?.options?.find(
                    (responseOption) => responseOption?.isSelected
                  );

                  if (approvedOption) {
                    draft.items[categoryIndex].approvedSelectionAmountDecimalForm =
                      getTotalSelectionsAmount(
                        draft.items[categoryIndex].approvedSelectionAmountDecimalForm,
                        Number(approvedOption.amount) * approvedOption.quantity,
                        'sum'
                      );
                  }
                }
                // add created selection
                draft.items[categoryIndex].selections.unshift(response);
              }
            }
          }
        )
      );
    };

    const createSelectionItem = ({
      response,
      projectId,
    }: {
      response: ISelectionItem;
      projectId: number;
    }) => {
      invalidateSelectionSummary();

      dispatch(
        selectionsApi.util.updateQueryData(
          'getCostCodesWithSelections',
          {
            projectId,
          },
          (draft) => {
            if (draft?.items) {
              const categoryIndex = draft.items.findIndex(({ id }) => id === response.costCode.id);
              if (categoryIndex >= 0) {
                // update approved amount
                if (response?.status === SelectionStatusEnum.APPROVED) {
                  const approvedOption = response?.options?.find(
                    (responseOption) => responseOption?.isSelected
                  );

                  if (approvedOption) {
                    draft.items[categoryIndex].approvedSelectionAmountDecimalForm =
                      getTotalSelectionsAmount(
                        draft.items[categoryIndex].approvedSelectionAmountDecimalForm,
                        Number(approvedOption.amount) * approvedOption.quantity,
                        'sum'
                      );
                  }
                }
                // add created selection
                draft.items[categoryIndex].selections.unshift(response);
              }
            }
          }
        )
      );
    };

    const updateSelectionItem = ({
      response,
      meta,
      projectId,
    }: {
      response: ISelectionItem;
      meta: IUpdatedSelectionItemMeta;
      projectId: number;
    }) => {
      invalidateSelectionSummary();

      const { oldCategoryId, selectionId, oldStatus, oldTotalAmount, newStatus } = meta;

      dispatch(
        selectionsApi.util.updateQueryData(
          'getCostCodesWithSelections',
          {
            projectId,
          },
          (draft) => {
            if (draft?.items) {
              if (oldCategoryId !== response?.costCode?.id) {
                const deletableSelectionCostCodeIndex = draft.items.findIndex(
                  ({ id }) => id === oldCategoryId
                );
                if (deletableSelectionCostCodeIndex >= 0) {
                  dispatch(
                    unselectSelection({
                      costCodeId: String(oldCategoryId),
                      selectionId,
                    })
                  );
                  // delete item from cost code
                  draft.items[deletableSelectionCostCodeIndex].selections = draft.items[
                    deletableSelectionCostCodeIndex
                  ].selections?.filter(({ id: sId }) => sId !== selectionId);
                  // reduce total amount for cost code
                  if (oldStatus === SelectionStatusEnum.APPROVED && oldTotalAmount) {
                    draft.items[
                      deletableSelectionCostCodeIndex
                    ].approvedSelectionAmountDecimalForm = getTotalSelectionsAmount(
                      draft.items[deletableSelectionCostCodeIndex]
                        .approvedSelectionAmountDecimalForm,
                      oldTotalAmount,
                      'subtract'
                    );
                  }
                }

                const addableSelectionCostCodeIndex = draft.items?.findIndex(
                  ({ id }) => id === response?.costCode?.id
                );
                if (addableSelectionCostCodeIndex >= 0 && response?.costCode?.id) {
                  // add to cost code
                  draft.items[addableSelectionCostCodeIndex].selections?.unshift(response);
                  dispatch(
                    selectSelection({
                      costCodeId: String(response.costCode.id),
                      selection: response,
                    })
                  );
                  // increase category total amount
                  if (newStatus === SelectionStatusEnum.APPROVED) {
                    const responseSelectedOption = response?.options?.find(
                      ({ isSelected }) => isSelected
                    );

                    if (responseSelectedOption) {
                      draft.items[
                        addableSelectionCostCodeIndex
                      ].approvedSelectionAmountDecimalForm = getTotalSelectionsAmount(
                        draft.items[addableSelectionCostCodeIndex]
                          .approvedSelectionAmountDecimalForm,
                        Number(responseSelectedOption.amount) * responseSelectedOption.quantity,
                        'sum'
                      );
                    }
                  }
                }
              } else {
                const costCodeIndex = draft.items?.findIndex(({ id }) => id === oldCategoryId);
                if (costCodeIndex >= 0) {
                  // update cost code total allowance
                  if (
                    oldStatus === SelectionStatusEnum.PENDING &&
                    newStatus === SelectionStatusEnum.APPROVED
                  ) {
                    // increase (from response)
                    const responseSelectedOption = response?.options?.find(
                      ({ isSelected }) => isSelected
                    );

                    if (responseSelectedOption) {
                      draft.items[costCodeIndex].approvedSelectionAmountDecimalForm =
                        getTotalSelectionsAmount(
                          draft.items[costCodeIndex].approvedSelectionAmountDecimalForm,
                          Number(responseSelectedOption.amount) * responseSelectedOption.quantity,
                          'sum'
                        );
                    }
                  } else if (
                    oldStatus === SelectionStatusEnum.APPROVED &&
                    newStatus === SelectionStatusEnum.APPROVED
                  ) {
                    const responseSelectedOption = response?.options?.find(
                      ({ isSelected }) => isSelected
                    );

                    if (responseSelectedOption && oldTotalAmount) {
                      draft.items[costCodeIndex].approvedSelectionAmountDecimalForm =
                        getTotalSelectionsAmount(
                          draft.items[costCodeIndex].approvedSelectionAmountDecimalForm,
                          oldTotalAmount -
                            Number(responseSelectedOption.amount) * responseSelectedOption.quantity,
                          'subtract'
                        );
                    }
                  } else if (
                    oldStatus === SelectionStatusEnum.APPROVED &&
                    newStatus === SelectionStatusEnum.PENDING &&
                    oldTotalAmount
                  ) {
                    // reduce (from old total)
                    draft.items[costCodeIndex].approvedSelectionAmountDecimalForm =
                      getTotalSelectionsAmount(
                        draft.items[costCodeIndex].approvedSelectionAmountDecimalForm,
                        oldTotalAmount,
                        'subtract'
                      );
                  }

                  const selectionIndex = draft.items[costCodeIndex].selections?.findIndex(
                    ({ id }) => id === selectionId
                  );

                  if (selectionIndex >= 0) {
                    // update selection item
                    draft.items[costCodeIndex].selections[selectionIndex] = response;
                  }

                  dispatch(
                    updateSelectedSelection({
                      costCodeId: String(response.costCode.id),
                      selection: response,
                    })
                  );
                }
              }
            }
          }
        )
      );
    };

    const deleteSelectionItem = ({
      meta,
      projectId,
    }: {
      meta: TDeletedSelectionItemMeta;
      projectId: number;
    }) => {
      invalidateSelectionSummary();

      const { categoryId, id, totalAmount, status } = meta;

      dispatch(
        selectionsApi.util.updateQueryData('getCostCodesWithSelections', { projectId }, (draft) => {
          if (draft?.items) {
            const costCodeIndex = draft.items?.findIndex(({ id: cId }) => cId === categoryId);
            if (costCodeIndex >= 0) {
              draft.items[costCodeIndex] = {
                ...draft.items[costCodeIndex],
                selections: draft.items[costCodeIndex].selections?.filter(
                  ({ id: selectionId }) => id !== selectionId
                ),
              };
            }
            if (totalAmount && status === SelectionStatusEnum.APPROVED && costCodeIndex >= 0) {
              draft.items[costCodeIndex].approvedSelectionAmountDecimalForm =
                getTotalSelectionsAmount(
                  draft.items[costCodeIndex].approvedSelectionAmountDecimalForm,
                  totalAmount,
                  'subtract'
                );
            }
          }
        })
      );

      dispatch(
        unselectSelection({
          costCodeId: String(categoryId),
          selectionId: id,
        })
      );
    };

    const invalidateCostCodes = (projectId: number) => {
      const cursor = (store.getState() as RootState).queryArgs.selectionCategories?.cursor;
      if (cursor) {
        dispatch(setDefaultArgs());
      } else {
        dispatch(
          selectionsApi.util.invalidateTags([
            'Selections Summary',
            { type: 'Project Cost Codes', id: projectId },
          ])
        );
      }
    };

    const updateCostCode = ({
      response,
      projectId,
    }: {
      response: ICostCode;
      projectId: number;
    }) => {
      invalidateSelectionSummary();

      dispatch(
        selectionsApi.util.updateQueryData('getCostCodesWithSelections', { projectId }, (draft) => {
          if (draft?.items) {
            const index = draft.items?.findIndex(({ id }) => id === response.id);
            if (index >= 0) {
              Object.assign(draft.items[index], response);
            }
          }
        })
      );
    };

    const createCostCode = ({
      response,
      projectId,
    }: {
      response: ICostCode;
      projectId: number;
    }) => {
      invalidateSelectionSummary();

      dispatch(
        selectionsApi.util.updateQueryData('getCostCodesWithSelections', { projectId }, (draft) => {
          if (draft?.items) {
            draft.items.unshift({
              ...response,
              selections: [],
              approvedSelectionAmountDecimalForm: '0',
            });
          }
        })
      );
    };

    const deleteCostCode = ({ response, projectId }: { response: number; projectId: number }) => {
      invalidateSelectionSummary();

      dispatch(
        selectionsApi.util.updateQueryData('getCostCodesWithSelections', { projectId }, (draft) => {
          if (draft?.items) {
            const index = draft.items?.findIndex(({ id }) => id === response);
            if (index >= 0) {
              draft.items = draft.items?.filter(({ id }) => id !== response);
            }
            dispatch(unselectCostCode({ costCodeId: String(response) }));
          }
        })
      );
    };

    const approveSelectionItem = ({
      response,
      meta,
      projectId,
    }: {
      response: ISelectionItem;
      meta: TApprovedSelectionItemMeta;
      projectId: number;
    }) => {
      invalidateSelectionSummary();

      const { approvedAt, approvedByProfile, options } = response;
      const { categoryId, optionIds, id, totalAmount } = meta;

      dispatch(
        selectionsApi.util.updateQueryData('getCostCodesWithSelections', { projectId }, (draft) => {
          if (draft?.items) {
            const categoryIndex = draft.items?.findIndex(({ id: cId }) => cId === categoryId);
            if (categoryIndex >= 0) {
              // update selection item
              const selectionIndex = draft.items[categoryIndex].selections?.findIndex(
                ({ id: selectionId }) => selectionId === id
              );
              if (selectionIndex >= 0) {
                const selectedOption = optionIds[0];
                const variants =
                  options.find((option) => option.id === selectedOption)?.variants || [];
                const item = draft.items[categoryIndex].selections[selectionIndex];
                const newSelectionBody = {
                  ...item,
                  status: SelectionStatusEnum.APPROVED,
                  approvedAt: approvedAt,
                  approvedByProfile,
                  phaseStatus: SelectionPhaseStatusesEnum.APPROVED_STATUS,
                  options: item.options?.map((option) => {
                    if (option.id === selectedOption) {
                      return {
                        ...option,
                        variants,
                        isSelected: true,
                      };
                    }

                    return {
                      ...option,
                      isSelected: false,
                    };
                  }),
                };
                draft.items[categoryIndex].selections[selectionIndex] = newSelectionBody;
                dispatch(
                  updateSelectedSelection({
                    costCodeId: String(newSelectionBody.costCode.id),
                    selection: newSelectionBody,
                  })
                );
              }
              // update category total amount
              draft.items[categoryIndex].approvedSelectionAmountDecimalForm =
                getTotalSelectionsAmount(
                  draft.items[categoryIndex].approvedSelectionAmountDecimalForm,
                  totalAmount,
                  'sum'
                );
            }
          }
        })
      );
    };

    const disapproveSelectionItem = ({
      response,
      projectId,
    }: {
      response: ISelectionItem;
      projectId: number;
    }) => {
      invalidateSelectionSummary();

      const categoryId = response.costCode.id;
      const selectionId = response.id;

      dispatch(
        selectionsApi.util.updateQueryData('getCostCodesWithSelections', { projectId }, (draft) => {
          if (draft?.items) {
            const categoryIndex = draft.items?.findIndex(({ id }) => id === categoryId);
            if (categoryIndex >= 0) {
              // update category total allowance
              const responseSelectedOption = response?.options?.find(
                ({ isSelected }) => isSelected
              );
              if (responseSelectedOption) {
                draft.items[categoryIndex].approvedSelectionAmountDecimalForm =
                  getTotalSelectionsAmount(
                    draft.items[categoryIndex].approvedSelectionAmountDecimalForm,
                    Number(responseSelectedOption.amount) * responseSelectedOption.quantity,
                    'subtract'
                  );
              }
              // update selection item
              const selectionIndex = draft.items[categoryIndex]?.selections?.findIndex(
                ({ id: sId }) => sId === selectionId
              );
              if (selectionIndex >= 0) {
                draft.items[categoryIndex].selections[selectionIndex] = Object.assign(
                  draft.items[categoryIndex].selections[selectionIndex],
                  response
                );
              }
              dispatch(
                updateSelectedSelection({
                  costCodeId: String(response.costCode.id),
                  selection: response,
                })
              );
            }
          }
        })
      );
    };

    const handleSelectionEvents = (eventData: TActivityFeedEvent) => {
      if ('profileId' in eventData && !getIsMe(eventData.profileId) && !isSelectionPage) return;

      if (eventData?.type === SelectionItemsEventEnum.SELECTION_ITEMS_CLONED) {
        cloneProjectSelection({
          response: eventData.data.selectionItems[0],
          projectId: eventData.projectId,
        });
      }

      if (eventData.type === SelectionItemsEventEnum.SELECTION_ITEMS_ADDED) {
        createSelectionItem({
          response: eventData.data.selectionItems[0],
          projectId: eventData.projectId,
        });
      }

      if (eventData.type === SelectionItemsEventEnum.SELECTION_ITEMS_UPDATED) {
        updateSelectionItem({
          response: eventData.data.selectionItems[0],
          meta: eventData.data.updatedSelectionItemsMeta[0],
          projectId: eventData.projectId,
        });
      }

      if (eventData.type === SelectionItemsEventEnum.SELECTION_ITEMS_DELETED) {
        const deletedSelectionItemsMeta = eventData.data.deletedSelectionItemsMeta;

        deletedSelectionItemsMeta
          ? deleteSelectionItem({
              meta: eventData.data.deletedSelectionItemsMeta?.[0],
              projectId: eventData.projectId,
            })
          : invalidateCostCodes(eventData.projectId);
      }

      if (eventData.type === SelectionItemsEventEnum.SELECTION_ITEMS_APPROVED) {
        const approvedSelectionItems = eventData.data.selectionItems;

        approvedSelectionItems.length > 1
          ? invalidateCostCodes(eventData.projectId)
          : approveSelectionItem({
              response: approvedSelectionItems[0],
              meta: eventData.data.approvedSelectionItemsMeta[0],
              projectId,
            });
      }

      if (eventData.type === SelectionItemsEventEnum.SELECTION_ITEMS_DISAPPROVED) {
        const disapprovedSelectionItems = eventData.data.selectionItems;

        disapprovedSelectionItems.length > 1
          ? invalidateCostCodes(eventData.projectId)
          : disapproveSelectionItem({
              response: disapprovedSelectionItems[0],
              projectId: eventData.projectId,
            });
      }

      if (eventData.type === SelectionItemsEventEnum.COST_CODES_UPDATED) {
        updateCostCode({ response: eventData.data.costCodes[0], projectId: eventData.projectId });
      }

      if (eventData.type === SelectionItemsEventEnum.COST_CODES_ADDED) {
        createCostCode({ response: eventData.data.costCodes[0], projectId: eventData.projectId });
      }

      if (eventData.type === SelectionItemsEventEnum.COST_CODES_DELETED) {
        deleteCostCode({
          response: eventData.data.costCodesIds[0],
          projectId: eventData.projectId,
        });
      }

      if (
        eventData.type === SelectionItemsEventEnum.SELECTION_ITEMS_IMPORT_LIBRARY ||
        eventData.type === SelectionItemsEventEnum.SELECTION_ITEMS_IMPORT_TEMPLATE ||
        eventData.type === SelectionItemsEventEnum.SELECTION_ITEMS_SET_REMINDER ||
        eventData.type === SelectionItemsEventEnum.COST_CODES_IMPORT
      ) {
        invalidateCostCodes(eventData.projectId);
      }
    };

    const messagesListener = (eventData: TActivityFeedEvent) => {
      if (isDraft(eventData)) {
        processDraftEvent(eventData);
        return;
      }

      if (eventData?.projectId && eventData.projectId === projectId) {
        handleSelectionEvents(eventData);

        if (eventData?.type === 'UPDATED_UPLOAD') {
          processUpdatedUpload(eventData);
        }

        if (eventData?.type === 'NEW_POST') {
          if (eventData?.data?.postUserIds?.includes(userMe.id)) {
            addPost(eventData.data, eventData.projectId);

            dispatch(deleteDraft({ id: eventData?.data?.id, type: 'DELETED_POST' }));

            addNotification(
              'messages',
              eventData.projectId,
              [eventData.projectId],
              userMe.id !== eventData.data?.data?.profile?.id
            );
          }
        }

        if (eventData?.type === 'NEW_COMMENT') {
          if (
            eventData?.data?.userIds?.includes(userMe.id) ||
            eventData?.data?.postUserIds?.includes(userMe.id)
          ) {
            increasePostCommentsCount(eventData.data, eventData.projectId);

            dispatch(deleteDraft({ id: eventData?.data?.id, type: 'DELETED_COMMENT' }));

            addNotification(
              'messages',
              eventData.projectId,
              [eventData.projectId],
              userMe.id !== eventData?.data?.profile?.id
            );

            if (
              searchParams.get('postId') &&
              Number(searchParams.get('postId')) === eventData?.data?.postId
            ) {
              const postId = Number(searchParams.get('postId'));

              increasePostCommentsCount(eventData.data, eventData.projectId, postId);

              addCommentToPost(eventData.data, postId);
            } else {
              addLocalCommentToPost(eventData.data, userMe.id === eventData?.data?.profile?.id);
            }
          }
        }

        if (eventData?.type === 'NEW_NOTIFICATION') {
          if (isEventTypeSelection(eventData.data)) {
            if (
              (eventData.data.event ===
                SelectionNotificationEventEnum.SELECTION_ITEM_HAS_BEEN_ADDED &&
                userMe.id !== eventData.data.selectionItem.createdByProfileId) ||
              (eventData.data.event ===
                SelectionNotificationEventEnum.SELECTION_ITEM_HAS_BEEN_APPROVED &&
                userMe.id !== eventData.data.selectionItem?.approvedByProfile?.id)
            ) {
              const filterCondition =
                eventData.data.event ===
                SelectionNotificationEventEnum.SELECTION_ITEM_HAS_BEEN_ADDED
                  ? eventData.data?.selectionItem?.createdByProfileId &&
                    eventData.data.selectionItem.createdByProfileId !== userMe.id
                  : eventData.data?.selectionItem?.approvedByProfile?.id !== userMe.id;

              addNotification(
                'notifications',
                eventData.projectId,
                [eventData.projectId],
                filterCondition
              );

              // handleSelectionEvent(eventData.data);
            }
          } else {
            if (
              eventData.data.event === 'SCHEDULE_ITEM_HAS_BEEN_COMPLETED' &&
              eventData.data.isOverdueItemCompleted
            ) {
              addNotification(
                'overdue',
                eventData.projectId,
                [eventData.projectId],
                true,
                ChangeNotificationCountType.INCREASE
              );
            }

            if (eventData.data.event === 'ITEM_OR_PHASE_HAS_BEEN_RESCHEDULED') {
              const newEndDate = dayjs(eventData.data.newEndDate);
              const oldEndDate = dayjs(eventData.data.oldEndDate);
              const todayDate = dayjs();

              if (newEndDate.isBefore(todayDate) && !oldEndDate.isBefore(todayDate)) {
                dispatch(
                  projectsApi.util.invalidateTags([
                    { type: 'Unviewed Counts', id: eventData.projectId },
                  ])
                );
              }
            }

            addNotification(
              'notifications',
              eventData.projectId,
              [eventData.projectId],
              userMe.id !== eventData.data?.notification?.profileId
            );
          }
        }

        if (eventData?.type === 'CHANGED_POST') {
          if (eventData?.profileId !== userMe?.id) {
            updatePost(eventData.data, eventData.projectId);
          }
        }

        if (eventData?.type === 'CHANGED_COMMENT') {
          if (eventData?.profileId !== userMe?.id) {
            updateComment(eventData.data);
          }
        }

        if (eventData?.type === 'DELETED_COMMENT') {
          if (eventData?.data?.profileId !== userMe?.id) {
            if (eventData?.data?.activityFeedItemIds?.[0]) {
              const { activityFeedItemId, postId, rootCommentId } =
                eventData.data.activityFeedItemIds[0];
              const { deletedCommentsCount } = eventData.data;
              deleteComment({
                activityFeedItemId,
                postId,
                eventProjectId: eventData.projectId,
                rootCommentId,
                deletedCommentsCount,
              });
            }
          }
        }

        if (eventData?.type === 'DELETED_POST') {
          if (eventData?.data?.profileId !== userMe?.id) {
            if (eventData?.data?.activityFeedItemIds?.[0]) {
              const { activityFeedItemId } = eventData.data.activityFeedItemIds[0];
              deletePost(activityFeedItemId, eventData?.projectId);
            }
          }
        }

        if (eventData?.type === 'LIKED_POST' || eventData.type === 'UNLIKED_POST') {
          if (
            eventData?.data?.profileId !== userMe?.id &&
            eventData?.data?.activityFeedItemId &&
            eventData?.projectId
          ) {
            likeUnlikePost(
              eventData?.data?.activityFeedItemId,
              eventData?.projectId,
              eventData.type === 'LIKED_POST' ? 'like' : 'unlike'
            );
          }
        }

        if (eventData?.type === 'LIKED_COMMENT' || eventData?.type === 'UNLIKED_COMMENT') {
          if (
            eventData?.data?.profileId !== userMe?.id &&
            eventData?.data?.activityFeedItemId &&
            eventData?.projectId
          ) {
            likeUnlikeComment(
              eventData?.data?.activityFeedItemId,
              eventData?.projectId,
              eventData?.data?.postId,
              eventData?.type === 'LIKED_COMMENT' ? 'like' : 'unlike',
              eventData?.data?.rootCommentId
            );
          }
        }
      } else if (
        eventData?.projectId &&
        projectIds?.length &&
        projectIds.includes(eventData.projectId)
      ) {
        switch (eventData.type) {
          case 'NEW_POST':
            addNotification(
              'messages',
              eventData.projectId,
              projectIds,
              userMe.id !== eventData.data?.data?.profile?.id &&
                eventData?.data?.postUserIds?.includes(userMe.id)
            );
            break;
          case 'NEW_COMMENT':
            addNotification(
              'messages',
              eventData.projectId,
              projectIds,
              userMe.id !== eventData?.data?.profile?.id &&
                eventData?.data?.userIds?.includes(userMe.id)
            );
            break;
          case 'NEW_NOTIFICATION':
            if (isEventTypeSelection(eventData.data)) {
              const filterCondition =
                eventData.data.event ===
                SelectionNotificationEventEnum.SELECTION_ITEM_HAS_BEEN_ADDED
                  ? eventData.data?.selectionItem?.createdByProfileId &&
                    eventData.data.selectionItem.createdByProfileId !== userMe.id
                  : eventData.data?.selectionItem?.approvedByProfile?.id !== userMe.id;

              addNotification('notifications', eventData.projectId, projectIds, filterCondition);
            } else {
              if (
                eventData.data.event === 'SCHEDULE_ITEM_HAS_BEEN_COMPLETED' &&
                eventData.data.isOverdueItemCompleted
              ) {
                addNotification(
                  'overdue',
                  eventData.projectId,
                  [eventData.projectId],
                  true,
                  ChangeNotificationCountType.INCREASE
                );
              }

              if (eventData.data.event === 'ITEM_OR_PHASE_HAS_BEEN_RESCHEDULED') {
                const newEndDate = dayjs(eventData.data.newEndDate);
                const oldEndDate = dayjs(eventData.data.oldEndDate);
                const todayDate = dayjs();

                if (newEndDate.isBefore(todayDate) && !oldEndDate.isBefore(todayDate)) {
                  dispatch(
                    projectsApi.util.invalidateTags([
                      { type: 'Unviewed Counts', id: eventData.projectId },
                    ])
                  );
                }
              }

              addNotification(
                'notifications',
                eventData.projectId,
                projectIds,
                userMe.id !== eventData.data?.notification?.profileId
              );
            }
            break;
          default:
            break;
        }
      }
    };

    const projectCreationListener = (eventData: TActivityFeedEvent) => {
      if (eventData.type === 'projectCreatorJoinToRoom') {
        activityFeedSocket.current.emit(SocketEvents.JOIN_TO_ROOM_EVENT, eventData);
      } else if (eventData.type === 'NEW_NOTIFICATION') {
        if (eventData?.projectId && eventData.projectId === projectId) {
          addNotification('notifications', eventData.projectId, [eventData.projectId]);
        } else if (
          eventData?.projectId &&
          projectIds?.length &&
          projectIds.includes(eventData.projectId)
        ) {
          addNotification('notifications', eventData.projectId, projectIds);
        }
      }
    };

    if (activityFeedSocket.current && userMe?.id) {
      activityFeedSocket.current.on(SocketEvents.SEND_MESSAGE_EVENT, messagesListener);
      activityFeedSocket.current.on(
        `${SocketEvents.ADD_TO_PROJECT_EVENT}/${userMe.id}`,
        projectCreationListener
      );
    }

    return () => {
      if (activityFeedSocket.current && activityFeedSocket.current.connected) {
        activityFeedSocket.current.off(SocketEvents.SEND_MESSAGE_EVENT, messagesListener);
        activityFeedSocket.current.off(
          `${SocketEvents.ADD_TO_PROJECT_EVENT}/${userMe?.id}`,
          projectCreationListener
        );
      }
    };
  }, [activityFeedSocket, projectId, projectIds, searchParams.get('postId'), userMe?.id]);

  type TBillingPlanEventType = 'created' | 'updated';
  type TBillingPlanEvent = {
    event: TBillingPlanEventType;
    company: ICompany;
    billingPlan: ICompanyBillingPlan;
  };

  useEffect(() => {
    const handleUpdatedCompanyBillingPlan = (event: TBillingPlanEvent) => {
      dispatch(billingApi.util.invalidateTags(['Current Plan']));
      if (event.billingPlan)
        dispatch(setSubscribedPlanInfo({ plan: event.billingPlan, isSubscribed: true }));
    };

    if (companyBillingSocket.current && userMe?.id) {
      companyBillingSocket.current.on(SocketEvents.UPDATED, (event: TBillingPlanEvent) =>
        handleUpdatedCompanyBillingPlan(event)
      );

      companyBillingSocket.current.on(SocketEvents.CREATED, (event: TBillingPlanEvent) =>
        handleUpdatedCompanyBillingPlan(event)
      );
    }
    return () => {
      if (companyBillingSocket.current && companyBillingSocket.current.connected) {
        companyBillingSocket.current.off(SocketEvents.CREATED, handleUpdatedCompanyBillingPlan);
        companyBillingSocket.current.off(SocketEvents.UPDATED, handleUpdatedCompanyBillingPlan);
      }
    };
  }, [companyBillingSocket, userMe?.id]);

  return null;
};
